/* eslint-disable no-console */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';
import '../../themes/quill-nile.css';
import getI18nService from '../../i18n/i18n';
import { CustomTypography } from '../../common/CustomTypoGraphy';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import { countryCodes } from '../../common/metadata/DataEnums';
import TermsAndConditions from '../includes/TermsAndConditions';
const i18n = getI18nService();

const phoneUtil = PhoneNumberUtil.getInstance();

const MobileNumber = ({ requiredInput, onSubmit, setShowPasscodeForm, setPhoneNumber, editorHtml }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  //   const [state, dispatch] = useContext(Context);
  const { phoneState, setPhoneState } = setPhoneNumber;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [errMesg, setErrMesg] = useState({
    phoneNum: '',
    name: '',
    email: '',
  });
  const [showTncForEmail, setShowTncForEmail] = useState(false);
  //   const getCountryByDialCode = dialCode => {
  //     const country = countryCodes.find(c => c.dialCode === dialCode || c.dialCode === `+${dialCode}`);
  //     return country ? country.code : null; // Return ISO code or null if not found
  //   };

  const validateEmail = email => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (number, code, regexStr = '') => {
    try {
      if (regexStr) {
        return regexStr?.test(number);
      }
      const parsedNumber = phoneUtil.parse(number, code);
      return phoneUtil.isValidNumber(parsedNumber);
    } catch (e) {
      return false;
    }
  };

  //   const validatePhoneForm = (phoneNumber, countryCode) => {
  const validatePhoneForm = () => {
    const { phoneNumber, countryCode } = phoneState;
    let isValid = true;

    // Check if both phone number and country code are provided and valid
    if (phoneNumber && countryCode) {
      const regexStr = countryCodes.find(c => c.code === countryCode)?.regex;
      isValid = validatePhoneNumber(phoneNumber, countryCode, regexStr);
      if (!isValid) {
        setErrMesg({ ...errMesg, phoneNum: 'Invalid phone number.' });
      } else {
        setErrMesg({ ...errMesg, phoneNum: '' });
      }
    } else {
      isValid = false;
    }

    // Check email validation if email is provided
    if (email) {
      const emailValid = validateEmail(email);
      if (!emailValid) {
        setErrMesg({ ...errMesg, email: 'Invalid email.' });
        isValid = false;
      } else {
        setErrMesg({ ...errMesg, email: '' });
      }
    }

    // Check SMS consent checkbox - must be checked to enable button
    if (!smsConsent) {
      isValid = false;
    }

    if (requiredInput?.name && !name) {
      isValid = false;
    }

    if (requiredInput?.email && !email) {
      isValid = false;
    }

    setIsFormValid(isValid);
  };

  useEffect(() => {
    validatePhoneForm();
  }, [phoneState, name, email, smsConsent]);

  const handleSubmit = () => {
    if (phoneState.countryCode && phoneState.phoneNumber) {
      const cCode = countryCodes.find(c => c.code === phoneState.countryCode);
      onSubmit({ phoneNumber: phoneState.phoneNumber, countryCode: cCode, name, email });
    }
  };

  const showTermsDialog = event => {
    console.log('SHOW NOW');
    setShowTncForEmail(true);
  };
  const hideTermsDialog = event => {
    console.log('SHOW NOW');
    setShowTncForEmail(false);
  };

  if (showTncForEmail) {
    return <TermsAndConditions editorHtml={editorHtml} withClose={true} onClose={hideTermsDialog} />;
  }
  return (
    <>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          height: 90,
          // mx: 2,
          pt: 4,
        }}
      >
        <TextField
          select
          label="Country"
          value={phoneState.countryCode}
          onChange={e => setPhoneState({ ...phoneState, countryCode: e.target.value })}
          size="small"
          sx={{
            width: { xs: 92, sm: 117 },
            height: 56,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.text.dark,
              },
            },
          }}
          required
        >
          {countryCodes.map(option => (
            <MenuItem key={option.code} value={option.code}>
              {option.dialCode}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Phone Number"
          value={phoneState.phoneNumber || ''}
          onChange={e => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            setPhoneState({ ...phoneState, phoneNumber: value });
          }}
          error={errMesg.phoneNum && !isFormValid}
          helperText={errMesg.phoneNum}
          sx={{
            width: { xs: 258, sm: 445 },
            height: 56,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.text.dark,
              },
            },
          }}
          size="small"
          required
        />
      </Box>

      {requiredInput?.name && (
        <TextField
          label="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.text.dark,
              },
            },
          }}
          size="small"
          required
        />
      )}

      {requiredInput?.email && (
        <TextField
          label="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          error={errMesg.email && !isFormValid}
          helperText={errMesg.email}
          sx={{
            width: '100%',
            mt: 2,
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.text.dark,
              },
            },
          }}
          size="small"
          required
        />
      )}

      <Box display="flex" width="100%" justifyContent="center" textAlign="center" ml={0}>
        <FormControlLabel
          control={
            <Checkbox
              checked={smsConsent}
              onChange={e => setSmsConsent(e.target.checked)}
              size="small"
              sx={{
                color: theme.palette.text.white,
                padding: '0px',
                mr: 0,
              }}
            />
          }
          label={
            <CustomTypography sx={{ color: theme.palette.text.white, fontSize: { xs: 14, sm: 16 }, ml: 1 }}>
              I consent to receive text messages. Message and data rates may apply.
            </CustomTypography>
          }
          sx={{
            margin: 'auto',
            alignItems: 'flex-start',
            '& .MuiFormControlLabel-label': {
              lineHeight: 1.43,
            },
          }}
        />
      </Box>

      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        width="100%"
        gap={1}
        mt={1}
        textAlign="center"
      >
        <CustomTypography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 500 }}>
          By registering, I accept the{' '}
          <Link to="/#" sx={{ textDecoration: 'none', cursor: 'pointer' }} onClick={showTermsDialog}>
            Terms of service
          </Link>
        </CustomTypography>
        <CustomTypography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 500 }}>
          <Link
            to="/#"
            onClick={e => {
              e.preventDefault();
              setShowPasscodeForm(true);
            }}
            sx={{ textDecoration: 'none', cursor: 'pointer' }}
          >
            {i18n.t('Messages.alreadyHaveCode')}
          </Link>
        </CustomTypography>
      </Box>

      <Box display="flex" justifyContent="center" width="100%" mt={3}>
        <Button
          variant="contained"
          color="primary"
          disabled={!isFormValid}
          sx={{
            width: '274px',
            height: '40px',
            textTransform: 'none',
          }}
          onClick={handleSubmit}
        >
          {i18n.t('Messages.getAccessCode')}
        </Button>
      </Box>
    </>
  );
};

MobileNumber.propTypes = {
  editorHtml: PropTypes.object,
  requiredInput: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  setShowPasscodeForm: PropTypes.func.isRequired,
  setPhoneNumber: PropTypes.object.isRequired,
};

MobileNumber.defaultProps = {
  editorHtml: {},
};

export default MobileNumber;
